import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Zap, Lock } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const PAID = new Set(["active", "pro_tier", "active_business"]);
  if (!loading && user) {
    if (!PAID.has(user.subscription_status)) navigate("/paywall", { replace: true });
    else navigate("/dashboard", { replace: true });
  }

  const handleGoogleLogin = () => {
    // Route via /auth/callback so we don't collide with /paywall or /dashboard guards
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid-bg flex flex-col">
      <header className="px-8 py-6 flex items-center justify-between">
        <Logo size="md" />
        <button
          onClick={() => navigate("/")}
          data-testid="login-back-btn"
          className="text-slate-400 hover:text-white text-sm"
        >
          ← Back
        </button>
      </header>

      <div className="flex-1 grid lg:grid-cols-2 gap-12 px-8 pb-16 items-center max-w-[1400px] mx-auto w-full">
        {/* Left: pitch */}
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-widest mb-6">
            <Zap size={12} /> Premium Ledger
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-white">
            The receipt vault built for{" "}
            <span className="text-emerald-400">audit-proof</span> filing.
          </h1>
          <p className="text-slate-300 mt-6 text-lg leading-relaxed max-w-lg">
            Snap, forward, or sync — Receipt Sheets uses GPT-5.2 Vision to extract
            every dollar, line-item and tax code with{" "}
            <span className="text-emerald-300 font-mono">99.4%</span> accuracy.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 stagger">
            {[
              { icon: ShieldCheck, label: "IRS-compliant archives" },
              { icon: Zap, label: "GPT-5.2 OCR magic" },
              { icon: Lock, label: "Bank-grade encryption" },
            ].map((f) => (
              <div
                key={f.label}
                className="glass rounded-xl p-4 flex items-start gap-3"
              >
                <f.icon size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                <span className="text-sm text-slate-200">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: auth card */}
        <div className="fade-up flex justify-center lg:justify-end" style={{ animationDelay: "0.15s" }}>
          <div className="glass rounded-3xl p-10 w-full max-w-md">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-white">
                Sign in to continue
              </h2>
              <p className="text-slate-400 text-sm mt-2">
                Premium plan required after sign-in.
              </p>
            </div>

            <button
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
              className="w-full h-14 rounded-xl bg-white text-slate-900 font-semibold text-base flex items-center justify-center gap-3 hover:bg-slate-100 transition-colors"
            >
              <svg width="22" height="22" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            <p className="text-center text-slate-500 text-xs mt-6 leading-relaxed">
              By continuing you agree to our Terms of Service and Privacy Policy.
              Access to Receipt Sheets is premium-only.
            </p>

            <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-center gap-6 text-slate-500 text-xs font-mono uppercase tracking-widest">
              <span>SOC 2</span>
              <span>·</span>
              <span>256-bit AES</span>
              <span>·</span>
              <span>GDPR</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
